import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MAX_BATCH_FILES,
  createJobsFromPage,
  findPageImages,
  type CleanupPolicy,
  type PageImageSet,
  type UploadRejection,
} from '@/lib/packshots';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

type PageUrlPickerProps = {
  disabled: boolean;
  cleanupPolicy: CleanupPolicy;
  cleanupPreset: string;
  cutoutPreset: string;
  onCreated: (batchId: string, rejections: UploadRejection[]) => void;
};

export default function PageUrlPicker({
  disabled,
  cleanupPolicy,
  cleanupPreset,
  cutoutPreset,
  onCreated,
}: PageUrlPickerProps) {
  const [url, setUrl] = useState('');
  const [found, setFound] = useState<PageImageSet | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [finding, setFinding] = useState(false);
  const [creating, setCreating] = useState(false);
  const busy = disabled || finding || creating;

  async function onFind(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setFinding(true);
    setFound(null);
    setSelected([]);
    try {
      const next = await findPageImages(url);
      setFound(next);
      if (next.images.length === 0) toast.message('No product-sized images on that page');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not read that page');
    } finally {
      setFinding(false);
    }
  }

  function toggle(id: string) {
    setSelected((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      if (current.length >= MAX_BATCH_FILES) return current;
      return [...current, id];
    });
  }

  function onToggle(id: string) {
    if (!selected.includes(id) && selected.length >= MAX_BATCH_FILES) {
      toast.error('Only 10 images can be added at once');
      return;
    }
    toggle(id);
  }

  async function onCreate() {
    if (!found || selected.length === 0 || busy) return;
    setCreating(true);
    try {
      const result = await createJobsFromPage({
        importId: found.id,
        imageIds: selected,
        cleanupPolicy,
        cleanupPreset: cleanupPreset || undefined,
        cutoutPreset: cutoutPreset || undefined,
      });
      if (result.jobs.length === 0) {
        toast.error('None of the selected images could be used');
        return;
      }
      setFound(null);
      setSelected([]);
      setUrl('');
      onCreated(result.batchId, result.rejections);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create packshots');
    } finally {
      setCreating(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <form onSubmit={(event) => { void onFind(event); }} className="space-y-2">
        <Label htmlFor="page-url">Paste a product page URL</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id="page-url"
            type="url"
            inputMode="url"
            value={url}
            disabled={busy}
            onChange={(event) => setUrl(event.target.value)}
            required
          />
          <Button type="submit" disabled={busy || url.trim().length === 0}>
            {finding ? 'Finding images…' : 'Find images'}
          </Button>
        </div>
      </form>
      {found && found.images.length > 0 && (
        <div className="mt-4 space-y-3">
          {found.truncated && (
            <p className="text-xs text-muted-foreground">Some images were left out so this stays quick.</p>
          )}
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {found.images.map((image) => {
              const on = selected.includes(image.id);
              return (
                <li key={image.id}>
                  <button
                    type="button"
                    aria-pressed={on}
                    disabled={creating}
                    onClick={() => onToggle(image.id)}
                    className={cn(
                      'relative w-full overflow-hidden rounded-md border bg-muted text-left',
                      on ? 'border-primary ring-2 ring-primary' : 'border-border',
                    )}
                  >
                    <img
                      src={image.file}
                      alt=""
                      className="aspect-square w-full object-contain"
                    />
                    <span className="block truncate px-2 py-1 text-xs text-muted-foreground">
                      {image.width}×{image.height}
                    </span>
                    {on && (
                      <span className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-4" aria-hidden />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex justify-end">
            <Button type="button" disabled={creating || selected.length === 0} onClick={() => { void onCreate(); }}>
              {creating
                ? 'Creating…'
                : selected.length === 0
                  ? 'Create packshots'
                  : selected.length === 1
                    ? 'Create 1 packshot'
                    : `Create ${selected.length} packshots`}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
